// Step definitions for the spotlight tours (R34). Each step optionally anchors to an
// element tagged `data-tutorial="<anchor>"`; if that element is not on the page,
// SpotlightTour falls back to a centered card (never breaks).
//
// Two families of tours:
//  - Route tours (`home`, `exportar`, `editar`): auto-shown once per route and tracked
//    in `lib/usuarios.ts` (TUTORIAL_CATALOG) + DB completion. Keep those keys in sync.
//  - Modal tours (`modal-*`): contextual help launched ON DEMAND from the "Guía" button
//    inside complex modals. They never auto-show and never persist, so they are NOT in
//    TUTORIAL_CATALOG (they must not count toward the completion total).
//
// Copy is neutral Spanish (no regional forms).

import { MAX_UNIDADES_ADICIONALES } from '@siiv/schemas';

export type TutorialClave =
	| 'home'
	| 'exportar'
	| 'editar'
	// Modal tours come in two variants, picked at launch by item count:
	//  `-empty`  → the collection has no items: teach how to ADD one (the launcher
	//              opens the inline form first so its fields can be highlighted).
	//  `-filled` → the collection already has items: teach how to MANAGE them
	//              (privacy / edit / delete / undo), no clicks required.
	| 'modal-cargos-empty'
	| 'modal-cargos-filled'
	| 'modal-formacion-empty'
	| 'modal-formacion-filled'
	// Información personal has a SINGLE tour (no variants): it walks the special rows
	// of the idle list, so the Guía button is disabled while a form/confirm is open.
	| 'modal-info-personal'
	// Rol mostrado: a form modal (not a collection). One tour, conditional steps for
	// the cargos/formación sections (empty vs with options) and a live preview.
	| 'modal-rol';

/** Where the card should sit relative to the highlighted element. */
export type CardPlacement = 'below' | 'above' | 'rightOf' | 'leftOf';

/**
 * After an interactive click, briefly move the spotlight to `anchor` so the user
 * sees the result (the now-open sidebar/menu). `auto` advances on its own after
 * `ms`; `countdown` keeps the card and shows a countdown inside the primary
 * button, enabling "Siguiente" when it ends.
 */
export interface RevealAfterClick {
	anchor: string;
	ms: number;
	pad?: number;
	radius?: number;
	/**
	 * Delay (ms) before the CARD moves to the reveal target, so it slides there in
	 * one diagonal motion once the element settled (e.g. after the menu appears)
	 * instead of chasing it. Default ~300. Ignored when `sidebarFinal` is set.
	 */
	settleMs?: number;
	/**
	 * The card should move IMMEDIATELY to the right of the fully-expanded sidebar
	 * (computed from the `--sb-expanded` CSS var), independent of the expand
	 * animation — so it slides diagonally the instant the button is clicked.
	 */
	sidebarFinal?: boolean;
}

export interface TutorialStep {
	/** data-tutorial id of the element to highlight. Omit for a centered step. */
	anchor?: string;
	title: string;
	body: string;
	/** Optional footnote shown under the body in a smaller, lighter, italic style —
	 *  for secondary caveats (e.g. "will be reviewed by an admin"). Supports **bold**. */
	note?: string;
	/** The user must CLICK the highlighted element to advance (native action runs). */
	interactive?: boolean;
	/**
	 * Makes `interactive` conditional: the step stays interactive (must click the
	 * anchor) UNLESS this `data-tutorial` element already exists, in which case the
	 * required action is already done and the step shows an enabled "Siguiente"
	 * instead. E.g. the empty tour's "click Agregar" step: normally you click Agregar
	 * (which opens the form and advances), but if the form is ALREADY open — because
	 * you launched the guide from create/edit mode — the same step 1 just shows
	 * "Siguiente". The step number stays 1 in both cases (no confusing jump to 2).
	 */
	interactiveUnlessPresent?: string;
	/** Highlight shape around the element. Default 'rect'. */
	shape?: 'rect' | 'circle';
	/** Corner radius (px) for a rect hole. Default 12. Use 0 for sharp corners. */
	radius?: number;
	/** Padding (px) around the element for the spotlight hole. Default 8. */
	pad?: number;
	/** Preferred card placement (falls back to auto if it does not fit). */
	cardPlacement?: CardPlacement;
	/** Override the card width (px) for this step. Default 340. */
	cardWidth?: number;
	/** Reveal choreography after the interactive click. */
	revealAfterClick?: RevealAfterClick;
	/**
	 * How the reveal ends. 'auto' advances after `ms`; 'countdown' shows a timer in
	 * the primary button then enables "Siguiente"; 'manual' just shows the reveal
	 * with an enabled "Siguiente". Default 'auto'.
	 */
	revealMode?: 'auto' | 'countdown' | 'manual';
	/** Card title/body shown during the reveal phase (falls back to title/body). */
	revealTitle?: string;
	revealBody?: string;
	/**
	 * The sidebar state this step expects on entry. The tour reconciles it when the
	 * step is shown (collapsing/expanding + closing the user menu), so navigating
	 * back does not leave the world in a state that makes the step nonsensical.
	 */
	requiredSidebar?: 'collapsed' | 'expanded';
	/**
	 * Extra elements to also light up (spotlight) besides `anchor` — highlighted and
	 * clickable, but WITHOUT a card. Used e.g. to show that toggling a section in the
	 * config also affects its block in the preview. Multi-hole steps do not block
	 * clicks (the user interacts freely and the step advances via `advanceOnEvent`).
	 */
	extraAnchors?: string[];
	/**
	 * Advance when the app dispatches a `siiv-tutorial-advance` CustomEvent whose
	 * `detail` equals this value (see dispatchTutorialAdvance). For steps completed
	 * by an app action (toggling a section, reordering by drag) rather than a click
	 * on the anchor. The primary button shows a disabled hint until it fires.
	 */
	advanceOnEvent?: string;
	/** Hint icon on the disabled primary button for interactive/event steps. */
	hint?: 'click' | 'drag';
	/**
	 * On entering this step, dispatch a `siiv-tutorial-enter` CustomEvent with this
	 * detail so the app can reconcile state the step depends on — e.g. re-enabling
	 * the "Presentación" section when the user comes back to the step that
	 * highlights it (an already-removed section can't be highlighted).
	 */
	onEnterEvent?: string;
	/**
	 * After the advancing action fires, wait this long (ms) before moving on, so the
	 * user sees the result of what they did. Hidden delay — no visible countdown.
	 */
	advanceDelayMs?: number;
	/**
	 * For event-driven steps: instead of auto-advancing after the action, ENABLE the
	 * "Siguiente" button so the user inspects the result and advances at their own
	 * pace. Ignored when `revealAfterClick` is set (the reveal already waits).
	 */
	waitForNextAfterEvent?: boolean;
	/**
	 * Conditional element: when it exists in the DOM it is ALSO highlighted (like an
	 * extraAnchor) and `body` is used; when absent, `bodyWithout` is used and it is
	 * not highlighted. E.g. the "Líneas de investigación" panel is hidden for unidad
	 * "Otra", so the enlaces step mentions it only when present.
	 */
	condAnchor?: string;
	/** Body text used when `condAnchor` is NOT present. */
	bodyWithout?: string;
	/** Card title used when `condAnchor` is NOT present. */
	titleWithout?: string;
	/**
	 * Footnote shown ONLY when `condAnchor` is NOT present (the `bodyWithout` case).
	 * Use it for a caveat that only makes sense in the empty state — e.g. defining
	 * what counts as a "cargo activo" / "formación terminada" when the user has none.
	 * A plain `note` still shows in both cases; this replaces it when absent.
	 */
	noteWithout?: string;
	/**
	 * Include this step ONLY if the given `data-tutorial` element exists when the tour
	 * starts; otherwise it is dropped from the tour entirely (so the step count stays
	 * right — no empty step, no gap in the numbering). E.g. the "Tu grado" step, which
	 * makes no sense until a formación derives a grado.
	 */
	onlyIfPresent?: string;
	/**
	 * Render a small mockup INSIDE the guide card (below the body). `'rol-header'` shows
	 * how the profile header looks: the investigator's name (from the launcher's
	 * `previewName`) with two placeholder lines, "Cargo" and "Formación".
	 */
	preview?: 'rol-header';
}

export const TUTORIAL_STEPS: Record<TutorialClave, TutorialStep[]> = {
	home: [
		{
			title: '¡Te damos la bienvenida a InvestigaUDA!',
			body: 'Te mostramos lo esencial en unos pasos rápidos. Puedes saltarlo cuando quieras.',
		},
		{
			anchor: 'home-sidebar',
			pad: 0,
			radius: 0,
			requiredSidebar: 'collapsed',
			title: 'Barra lateral',
			body: 'Desde aquí navegas por todo: inicio, el listado de investigadores, tu perfil y más.',
		},
		{
			anchor: 'home-sidebar-toggle',
			radius: 10,
			interactive: true,
			cardPlacement: 'rightOf',
			requiredSidebar: 'collapsed',
			revealAfterClick: { anchor: 'home-sidebar', ms: 0, pad: 0, radius: 0, sidebarFinal: true },
			revealMode: 'manual',
			revealTitle: 'Barra expandida',
			revealBody: 'Así se ve la barra abierta. Continúa cuando quieras.',
			title: 'Expandir la barra',
			body: 'Haz clic en este botón para expandir la barra lateral y ver las etiquetas.',
		},
		{
			anchor: 'home-user',
			pad: 0,
			radius: 0,
			interactive: true,
			cardPlacement: 'rightOf',
			requiredSidebar: 'expanded',
			revealAfterClick: { anchor: 'home-user-menu', ms: 0, pad: 6, radius: 12, settleMs: 150 },
			revealMode: 'manual',
			revealTitle: 'Tu menú',
			revealBody: 'Desde aquí puedes ir a tu perfil o cerrar sesión.',
			title: 'Tu cuenta',
			body: 'Haz clic aquí para abrir tu menú.',
		},
		{
			anchor: 'home-help',
			requiredSidebar: 'expanded',
			title: 'Botón de guía',
			body: '¿Te perdiste o quieres repasar algo? Este botón vuelve a abrir el tutorial de la vista en la te encuentres.',
		},
	],
	exportar: [
		{
			title: 'Exportar tu perfil',
			body: 'Puedes descargar tu CV/perfil para compartirlo o imprimirlo. Te mostramos cómo en unos pasos.',
		},
		{
			anchor: 'exportar-orden-presentacion',
			extraAnchors: ['exportar-preview-presentacion'],
			advanceOnEvent: 'exportar-toggle-presentacion',
			onEnterEvent: 'exportar-ensure-presentacion',
			waitForNextAfterEvent: true,
			hint: 'click',
			title: 'Elige qué secciones incluir',
			body: 'Cada sección tiene un interruptor. Al desmarcar “Presentación”, desaparece del documento de la derecha (resaltado). Pruébalo: quítala con el check o con el botón −.',
		},
		{
			anchor: 'exportar-info-personal',
			extraAnchors: ['exportar-preview-datos'],
			advanceOnEvent: 'exportar-reorder-personal',
			waitForNextAfterEvent: true,
			hint: 'drag',
			title: 'Ordena tus datos personales',
			body: 'Arrastra y suelta un bloque para cambiar su orden. A la derecha (resaltado) verás cómo cambia el documento.',
		},
		{
			anchor: 'exportar-accion',
			title: 'Descargar',
			body: 'Cuando esté listo, genera el archivo desde aquí (Imprimir / Guardar PDF).',
		},
	],
	editar: [
		{
			cardWidth: 300,
			title: 'Editar tu perfil',
			body: 'Este es el módulo de edición. Te mostramos lo principal para moverte con confianza.',
		},
		{
			anchor: 'editar-tabs',
			cardWidth: 312,
			title: 'Secciones',
			body: 'Cambia entre perfil, publicaciones y proyectos desde estas pestañas.',
		},
		{
			anchor: 'editar-identidad',
			cardWidth: 312,
			title: 'Tu identidad',
			body: 'Aquí están tu foto, nombre y rol. Con el botón “Editar” puedes modificarlos.',
		},
		{
			anchor: 'editar-afiliacion',
			cardWidth: 352,
			title: 'Tu afiliación',
			body: `Aquí ves tu pertenencia institucional: tu unidad **principal** y tus unidades **adicionales**. Con el **lápiz** abres un modal con **una fila por unidad**: puedes **cambiar el orden** de las adicionales y, si tienes la unidad “Otra”, **ponerle el nombre** que quieras. Esos cambios se aplican **al guardar**, sin solicitud. **Agregar, quitar o cambiar la unidad principal** (hasta ${MAX_UNIDADES_ADICIONALES} adicionales) se envía como **una sola solicitud** que revisa un administrador. Tus unidades se muestran todas en tu perfil público.`,
			note: 'Tu unidad se obtiene a partir de tu registro en la plataforma.',
		},
		{
			anchor: 'editar-info',
			cardWidth: 352,
			condAnchor: 'editar-lineas',
			title: 'Enlaces y líneas',
			titleWithout: 'Enlaces externos',
			body: 'Aquí gestionas tus líneas de investigación (las de la UDA y las que describes tú) y tus enlaces externos; cada sección tiene su lápiz.',
			bodyWithout: 'Aquí gestionas tus enlaces externos, agrega los que quieras.',
		},
		{
			anchor: 'editar-cargos',
			advanceOnEvent: 'editar-open-cargos',
			cardWidth: 352,
			advanceDelayMs: 256,
			onEnterEvent: 'editar-close-cargos',
			hint: 'click',
			title: 'Abre una sección',
			body: 'Cada sección del CV se edita en su propio modal. Probemos con Cargos Institucionales presionando sobre el ícono de lápiz.',
		},
		{
			anchor: 'editar-modal',
			pad: 8,
			radius: 14,
			cardWidth: 360,
			title: 'Editar una sección',
			body: 'Aquí agregas, editas y eliminas registros. Con “Deshacer Cambios” puedes revertir todo mientras el modal siga abierto.',
		},
		{
			anchor: 'modal-guia',
			cardPlacement: 'below',
			title: '¿Necesitas más ayuda?',
			body: 'En ciertos modales puedes presionar este botón y te guiaremos nuevamente.',
		},
	],
	// ── Cargos ──────────────────────────────────────────────────────────────
	'modal-cargos-empty': [
		{
			anchor: 'modal-add',
			interactive: true,
			hint: 'click',
			interactiveUnlessPresent: 'modal-form',
			cardPlacement: 'below',
			title: 'Agrega tu primer cargo',
			body: 'Presiona este botón para abrir el formulario y crear un cargo. Te explicaremos sus campos más importantes.',
		},
		{
			anchor: 'modal-field-cargo',
			cardPlacement: 'below',
			title: 'Cargo',
			body: 'Aquí puedes registrar el nombre del cargo que quieras que aparezca en tu curriculum institucional.',

		},
		{
			anchor: 'modal-field-tipo',
			cardWidth: 364,
			cardPlacement: 'below',
			title: 'Tipo de cargo',
			body: 'Si eliges un tipo de cargo diferente a tu estamento, este **cambiará automáticamente**. Será validado por un administrador.',
			note: 'Para que tu estamento cambie, el cargo agregado debe estar vigente.',
		},
		{
			anchor: 'modal-field-encurso',
			pad: 6,
			cardWidth: 380,
			cardPlacement: 'above',
			title: '¿Aún tienes este cargo?',
			body: 'Marca esta opción si es un **cargo vigente**. Si la dejas en blanco, se considera un **cargo histórico** y deberás indicar la **fecha de término**.',

		},
		{
			anchor: 'modal-form-save',
			cardPlacement: 'leftOf',
			title: 'Guardar',
			cardWidth: 380,
			body: 'Al guardar, el cargo queda registrado al instante.',
			note: 'Si tienes dudas de como gestionar tus cargos, pulsa nuevamente el botón "Guía"'
		},
	],
	'modal-cargos-filled': [
		{
			anchor: 'modal-action-privacy',
			pad: 3,
			radius: 999,
			cardPlacement: 'leftOf',
			cardWidth: 320,
			title: 'Privacidad',
			body: 'Con este botón decides si el cargo se **muestra** u **oculta** en tu perfil público.',
		},
		{
			anchor: 'modal-action-editdelete',
			pad: 3,
			radius: 11,
			cardPlacement: 'leftOf',
			title: 'Editar y eliminar',
			body: 'Con **Editar** modificas los datos del cargo, y con **Eliminar** lo quitas de tu perfil.',
		},
		{
			anchor: 'modal-undo',
			radius: 10,
			pad: 4,
			cardPlacement: 'above',
			title: 'Deshacer cambios',
			body: 'Si cambiaste algo por error y aún no has cerrado el modal, puedes revertir **todo lo que hayas hecho** presionando aquí.',
		},
	],
	// ── Formación académica ─────────────────────────────────────────────────
	'modal-formacion-empty': [
		{
			anchor: 'modal-add',
			interactive: true,
			hint: 'click',
			interactiveUnlessPresent: 'modal-form',
			cardWidth: 300,
			cardPlacement: 'below',
			title: 'Agrega tu primera formación',
			body: 'Presiona este botón para abrir el formulario y agregar un título o estudio.  Te explicaremos sus campos más importantes.',
		},
		{
			anchor: 'modal-field-titulo',
			cardPlacement: 'below',
			title: 'Título o grado',
			body: 'Aquí puedes registrar el nombre del título o grado que quieras que aparezca en tu curriculum institucional.',
		},
		{
			anchor: 'modal-field-categoria',
			cardPlacement: 'below',
			cardWidth: 364,
			title: 'Categoría',
			body: 'En este menú eliges la **categoría** de formación. Esto **genera** el grado que se muestra en tu información personal.',
			note: 'Solo Pregrado, Magíster y Doctorado generan grado.',
		},
		{
			anchor: 'modal-field-terminada',
			pad: 6,
			cardWidth: 356,
			cardPlacement: 'above',
			title: '¿Cursando actualmente?',
			body: 'Marca esta opción si aún estás cursando esta formación. En caso contrario, déjala en blanco e indica la **fecha de término**.',
			note: 'Las formaciones en curso no generan grado ni pueden aparecer como rol mostrado.',
		},
		{
			anchor: 'modal-form-save',
			cardPlacement: 'leftOf',
			cardWidth: 364,
			title: 'Guardar',
			body: 'Al guardar, la formación queda registrada al instante. Si corresponde, se generará el grado académico asociado.',
			note: 'Si tienes dudas de como gestionar tus formaciones, pulsa nuevamente el botón "Guía"'
		},
	],
	'modal-formacion-filled': [
		{
			anchor: 'modal-action-privacy',
			pad: 3,
			radius: 999,
			cardPlacement: 'leftOf',
			title: 'Privacidad',
			body: 'Con este botón decides si la formación se **muestra** u **oculta** en tu perfil público.',
		},
		{
			anchor: 'modal-action-editdelete',
			pad: 3,
			radius: 11,
			cardPlacement: 'leftOf',
			title: 'Editar y eliminar',
			body: 'Con **Editar** modificas los datos de la formación, y con **Eliminar** la quitas de tu perfil.',
		},
		{
			anchor: 'modal-undo',
			pad: 4,
			radius: 12,
			cardPlacement: 'above',
			title: 'Deshacer cambios',
			body: 'Revierte **todo lo que hayas hecho** mientras el modal siga abierto. Al cerrar, los cambios se guardan.',
		},
	],
	// ── Información personal ─────────────────────────────────────────────────
	'modal-info-personal': [
		{
			anchor: 'modal-add',
			extraAnchors: ['modal-add-bottom'],
			cardPlacement: 'below',
			title: 'Agregar datos',
			body: 'Puedes agregar datos desde estos botones.',
		},
		{
			anchor: 'modal-ip-rut',
			cardPlacement: 'rightOf',
			title: 'Tu RUT',
			cardWidth: 392,
			body: 'Siempre queda primero; no se **edita**, no se **elimina** y no se **mueve**. Por política, nunca se muestra en tu perfil público.',
			note: 'Este dato se obtiene a partir de tu registro en la plataforma.',
		},
		{
			anchor: 'modal-ip-grado',
			// Sin formación que derive un grado, la fila no existe: el paso se cae solo
			// (y la numeración se ajusta), en vez de mostrar una tarjeta sin sujeto.
			onlyIfPresent: 'modal-ip-grado',
			cardPlacement: 'rightOf',
			cardWidth: 364,
			title: 'Tu grado',
			body: 'Se **deriva** de tu formación académica. No se **edita** ni se **elimina** desde aquí. Cambia solo cuando agregas o terminas una formación.',
		},
		{
			anchor: 'modal-ip-correo',
			cardPlacement: 'rightOf',
			cardWidth: 392,
			title: 'Tu correo institucional',
			body: 'Representa el correo con el que te registraste. No se **edita** ni **elimina**, pero puedes elegir si se **muestra** o no en tu perfil público.',
			note: 'Este dato se obtiene a partir de tu registro en la plataforma.',
		},
		{
			anchor: 'modal-ip-preview',
			cardWidth: 380,
			cardPlacement: 'leftOf',
			title: 'Así se verá en tu perfil',
			body: 'En esta seccion podrás ordenar los elementos de tu información personal que quieres mostrar en tu **perfil público**. Arrastralos y visualiza como cambian en la vista previa.',
			note: 'Solo los datos marcados como “Público” aparecen aquí.',
		},
	],
	'modal-rol': [
		{
			title: 'Tu rol mostrado',
			cardWidth: 388,
			body: 'Es lo que aparece **debajo de tu nombre** en tu perfil público: puede ser un **cargo**, una **formación** o **ambos**. Aquí eliges cuáles.',
		},
		{
			anchor: 'modal-rol-cargos',
			condAnchor: 'modal-rol-cargos-options',
			cardPlacement: 'below',
			title: 'Cargo a mostrar',
			cardWidth: 428,
			body: 'Marca **“Mostrar un cargo”** para que aparezca debajo de tu nombre en tu perfil. Si tienes **varios activos**, debes elegir uno.',
			note: 'Un **cargo activo** es aquel que declaraste como **“Aún tengo este cargo”** en tu perfil.',
			bodyWithout: 'Aún no tienes cargos activos. Puedes agregar uno en la sección **“Cargos Insitucionales”**.',
			noteWithout: 'Un **cargo activo** es aquel que declaraste como **“Aún tengo este cargo”** en tu perfil.',
		},
		{
			anchor: 'modal-rol-formacion',
			condAnchor: 'modal-rol-formacion-options',
			cardPlacement: 'below',
			title: 'Formación a mostrar',
			cardWidth: 440,
			body: 'Marca **“Mostrar una formación”** para que aparezca debajo de tu nombre en tu perfil. Si tienes **varias formaciones terminadas**, debes elegir una.',
			note: 'Acá aparecen las formaciones que declaraste como **“terminadas”** en tu perfil. Solo se consideran las de categoría **pregrado**, **magíster** o **doctorado**.',
			bodyWithout: 'Aún no tienes formaciones **“terminadas”** que puedas mostrar. Puedes agregar una en la sección **Formación Académica**.',
			noteWithout: 'Solo cuentan las formaciones de categoría **pregrado**, **magíster** o **doctorado** que no hayas marcado como **“cursando actualmente”**.',
		},
		{
			cardWidth: 512,
			preview: 'rol-header',
			title: 'Así se verá bajo tu nombre',
			body: 'En tu perfil se mostrará el **cargo** y/o **formación** que elijas. Si no eliges ninguno, se mostrará tu **estamento**.',
		},
	],
};

/** Map a pathname to the tutorial that applies there (for the help button). */
export function tutorialForPath(pathname: string): TutorialClave | null {
	if (pathname === '/') return 'home';
	if (pathname.includes('/exportar')) return 'exportar';
	if (pathname.includes('/editar')) return 'editar';
	return null;
}
